import * as R from 'ramda'
import type { Nil } from '@pbt/pbt-ui-components'

import type {
  GeneratedMessageAttachmentsSubsection,
  GeneratedMessageAttachmentSubcategory,
  GeneratedMessageSectionAttachmentsInput,
  MedicalHistoryAttachmentsSection,
  MedicalHistoryBaseSection,
  MedicalHistoryConfig,
  MedicalHistorySection,
  MedicalHistorySectionInput,
  MedicalHistorySectionType,
  MedicalHistorySoapPreviewInput,
} from '~/api/graphql/generated/types'
import i18nPortal from '~/locales/i18n'
import type { EmailEntityConfig, EmailEntityConfigSection } from '~/types'

import { removePropsRecursively } from '.'

export function hasGrandChildWithId(
  child: EmailEntityConfigSection,
  id: string,
): boolean {
  return child.children?.some((grandChild) => grandChild.id === id) ?? false
}

export function isChildOrGrandChildWithId(
  child: EmailEntityConfigSection,
  id: string,
): boolean {
  return child.id === id || hasGrandChildWithId(child, id)
}

export const updateChecked = (
  id: string,
  sections: EmailEntityConfigSection[] | Nil,
  checked: boolean,
  forceUpdate = false,
): EmailEntityConfigSection[] => {
  const newSections = (sections || []).map((item: EmailEntityConfigSection) => {
    const newChildren = item.children
      ? updateChecked(id, item.children, checked, forceUpdate || item.id === id)
      : item.children

    const hasCheckedChildren = newChildren
      ? newChildren.some((child: EmailEntityConfigSection) => child.checked)
      : false

    const hasAllChildrenUnchecked = newChildren
      ? newChildren.every((child: EmailEntityConfigSection) => !child.checked)
      : false

    const isCurrentSection = item.id === id

    function shouldUncheckAllChildrenAndSection(): boolean {
      if (
        hasAllChildrenUnchecked &&
        checked === false &&
        newChildren?.some((child) => isChildOrGrandChildWithId(child, id))
      ) {
        return true
      }
      return false
    }

    const getNewChecked = () => {
      if (forceUpdate || isCurrentSection) {
        return checked
      }

      if (newChildren?.length) {
        // Only if all children are unchecked and the current item is being unchecked and it is part of the children
        if (shouldUncheckAllChildrenAndSection()) {
          return false
        }
        return hasCheckedChildren || item.checked
      }

      return item.checked
    }

    return {
      ...item,
      checked: getNewChecked(),
      children: newChildren,
    }
  })

  return newSections
}

const sectionTooltipMap: Record<MedicalHistorySectionType, string> = {
  LABS: i18nPortal.t('Tooltips:MEDICAL_HISTORY.LABS_SECTION_TOOLTIP'),
  ATTACHMENTS: i18nPortal.t(
    'Tooltips:MEDICAL_HISTORY.ATTACHMENTS_SECTION_TOOLTIP',
  ),
  ALERTS: '',
  APPOINTMENTS: '',
  DIAGNOSES: '',
  IMAGING: '',
  IMPORTED_HISTORY: '',
  OTC_SALE: '',
  PRESCRIPTIONS_DRUGS: '',
  QUESTIONS: '',
  REMINDERS: '',
  SURGERY: '',
  VACCINATIONS: '',
  NOTES: '',
  VITALS: '',
}

export const convertGraphqlToMedicalHistoryConfig = (
  queryConfigData: MedicalHistoryConfig | undefined,
): EmailEntityConfig | undefined => {
  if (!queryConfigData) {
    return undefined
  }

  return {
    message: queryConfigData?.message,
    subject: queryConfigData?.subject,
    sections: queryConfigData?.sections.map(
      (
        section: MedicalHistoryAttachmentsSection | MedicalHistoryBaseSection,
      ) => {
        const tooltip = sectionTooltipMap[section.section]

        if (
          section.__typename === 'MedicalHistoryAttachmentsSection' &&
          'children' in section &&
          Array.isArray(section.children) &&
          // We are only normalizing the LABS section as aligned with BE.
          // Like normalizing the added date, today the Attachments section returns the Added in the name
          // and we will remove in the future as debt tech
          section.section === 'LABS'
        ) {
          return {
            ...section,
            tooltip,
            defaultExpanded: true,
            children: section.children.map((child) => {
              const normalizedName = child.addedDate
                ? `${child.name} | ${i18nPortal.t('Common:ADDED')}`
                : child.name

              return {
                ...child,
                // We don't want to display the subcategory for this section
                hideSubcategory: true,
                name: normalizedName,
                children: child.children?.map(
                  (grandChild: GeneratedMessageAttachmentsSubsection) => ({
                    ...grandChild,
                    // Only display items as text
                    unselectable: true,
                  }),
                ),
              }
            }),
          }
        }

        return { ...section, tooltip }
      },
    ),
  }
}

export const collectAttachmentsRecursivelyFromChildren = (
  nodes?: EmailEntityConfigSection[] | null,
): GeneratedMessageSectionAttachmentsInput[] =>
  R.reduce<EmailEntityConfigSection, GeneratedMessageSectionAttachmentsInput[]>(
    (acc, node) => {
      const current: GeneratedMessageSectionAttachmentsInput = {
        id: node.id,
        subcategory: node.subcategory as GeneratedMessageAttachmentSubcategory,
      }
      const kids = collectAttachmentsRecursivelyFromChildren(
        node.children?.filter((it) => it.checked),
      )
      return R.concat(acc, R.prepend(current, kids))
    },
    [],
    nodes ?? [],
  )

export const convertMedicalHistoryConfigToPreviewInput = (
  config: EmailEntityConfig,
  patientId: string,
): MedicalHistorySoapPreviewInput => ({
  patientId: config.patientId || patientId,
  message: config.message || null,
  subject: config.subject || '',
  from: config.from,
  to: config.to,
  sections: config?.sections
    ?.filter((section) => section?.checked)
    ?.map(
      (section: any) =>
        ({
          section: (section as MedicalHistorySection)?.section,
          attachments: collectAttachmentsRecursivelyFromChildren(
            (section as EmailEntityConfigSection)?.children?.filter(
              (it) => it.checked,
            ),
          ),
        }) as MedicalHistorySectionInput,
    ),
})

export const normalizeSections = (
  sections: (EmailEntityConfigSection | null)[] | Nil,
) =>
  removePropsRecursively([
    '__typename',
    'hideSubcategory',
    'unselectable',
    'section',
    'defaultExpanded',
    'tooltip',
  ])(sections)
